import { LazyTemplate } from "../template";
import { BaseApp } from "../app";
import { EventSource } from "../event-source";
import AppHeader from "../app/header";
import ViewFilters from "./filter/index";
import ViewHelpers from "./helpers";
import { StringFormatArgs } from "../helpers/string";
import { BaseModal, ModalConstructor } from "../modal/index";

export abstract class Component extends EventSource
{
    public app: BaseApp;
    public template: LazyTemplate;
    public element: HTMLElement;
    
    public get defaults(): any { return {}; }
    public data: any;

    public filters = ViewFilters;
    public helpers = ViewHelpers;

    constructor(data?: any)
    {
        super();
        this.app = (window as any).Bluugo.app;
        let initialData = this.data != null ? this.data : this.defaults;
        data = data != null ? data : {};

        this.data = Object.assign(initialData, data);
    }

    public beforeRender(): PromiseLike<void>
    {
        this.trigger(ViewEvent.BEFORE_RENDER);
        return Promise.resolve();
    }

    public afterRender(): PromiseLike<void>
    {
        this.trigger(ViewEvent.AFTER_RENDER);
        return Promise.resolve();
    }

    /**
     * Runs a querySelectorAll against the view root element and the view root element itself.
     * @param selector The selector string.
     */
    public querySelector(selector: string): Element[]
    {
        let result: Element[] = [].slice.call(this.element.querySelectorAll(selector));
        if (this.element.matches(selector))
        {
            result.push(this.element);
        }

        return result;
    }

    /**
     * Adds an event listener and attaches it to a selector or an element.
     * @param elementOrSelector Selector or element.
     * @param eventName Name of the event.
     * @param handler Handler.
     */
    public addEventListener(elementOrSelector: Element | Element[] | string, eventName: string | string[], handler: (e: Event) => void): Element[]
    {
        let elements: Element[];
        let eventNames = Array.isArray(eventName) ? eventName : [eventName];

        if (typeof elementOrSelector == 'string')
        {
            elements = [].slice.call(this.element.querySelectorAll(elementOrSelector));
        }

        if (Array.isArray(elementOrSelector))
        {
            elements = elementOrSelector;
        }
        else
        {
            elements = [elementOrSelector as Element];
        }

        for (let element of elements)
        {
            for (let eventName of eventNames)
            {
                element.addEventListener(eventName, handler);
            }
        }
        return elements;
    }

    /**
     * Appends a view element to the view.
     * @param view The view to append.
     */
    public appendChild(view: Component)
    {
        this.element.appendChild(view.element);
    }

    public pgettext(context: string, message: string, args?: StringFormatArgs, autoEscape = true): string
    {
        return this.app.pgettext(context, message, args, autoEscape);
    }

    public ugettext(message: string, args?: StringFormatArgs): string
    {
        return this.app.ugettext(message, args);
    }
}

export abstract class View extends Component
{
    protected _isUnloaded: boolean;
    public get isUnloaded() { return this._isUnloaded; }

    constructor(data?: any)
    {
        super(data);
        this._isUnloaded = false;
    }

    public formSubmitted(form: HTMLFormElement): PromiseLike<void>
    {
        return this.submit(form);
    }
    
    public submit(data?: any): PromiseLike<void>
    {
        this.trigger(ViewEvent.SUBMIT);
        return this.unload();
    }

    public close(): PromiseLike<void>
    {
        this.trigger(ViewEvent.CLOSE);
        if (!this.isUnloaded)
        {
            return this.unload();
        }
        else
        {
            return Promise.resolve();
        }
    }
    
    public unload(): PromiseLike<void>
    {
        this.trigger(ViewEvent.UNLOAD);
        this._isUnloaded = true;
        return Promise.resolve();
    }

    /**
     * Helper for opening a modal in a view.
     * @param modalType Type of the modal (class).
     * @param sender Sender element of the modal opening, usually a button. It is automatically disabled.
     * @param data Data passed to the modal.
     * @param refreshViewData Data passed to the view refresh.
     */
    protected openModal(modalType: ModalConstructor, sender?: HTMLElement, data: Object = undefined, refreshViewData: Object = undefined)
    {
        data = data == null ? this.data : data;

        if (sender != null)
        {
            this.helpers.setButtonEnabled(sender, false);
        }

        let modal = new modalType(data);
        return this.app.modals.show(modal).then(() =>
        {
            if (sender != null)
            {
                this.helpers.setButtonEnabled(sender, true);
            }

            return this.app.refreshCurrentView(refreshViewData);
        }); 
    }
}

export interface ViewConstructor
{
    new (data?: any): View;
}

export enum ViewEvent
{
    UNLOAD = 'unload',
    CLOSE = 'close',
    SUBMIT = 'submit',
    BEFORE_RENDER = 'beforeRender',
    AFTER_RENDER = 'afterRender'
}